import { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { Add, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { templesService } from '../services/temples.service';
import { Temple } from '../types';
import { TemplesTable, TemplesToolbar } from '../components/temples/TemplesTable';
import { TemplesForm } from '../components/temples/TemplesForm';

export default function TemplesPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTemple, setEditingTemple] = useState<Temple | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const queryClient = useQueryClient();

  const {
    data: templesData,
    isLoading,
  } = useQuery({
    queryKey: ['temples', { page, limit, search }],
    queryFn: () => templesService.getList({ page, limit, search }),
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Temple, 'id' | 'createdAt' | 'updatedAt'>) => templesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['temples'] });
      setIsFormOpen(false);
      setEditingTemple(null);
      setNotification({ message: 'Храм добавлен', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при добавлении', type: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Temple> }) => templesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['temples'] });
      setIsFormOpen(false);
      setEditingTemple(null);
      setNotification({ message: 'Данные обновлены', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при обновлении', type: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => templesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['temples'] });
      setNotification({ message: 'Храм удален', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при удалении', type: 'error' });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (id: number) => templesService.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['temples'] });
      setNotification({ message: 'Статус изменен', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при изменении статуса', type: 'error' });
    },
  });

  const filteredTemples = templesData?.data || [];
  const total = templesData?.pagination?.total || 0;

  const handleCreate = () => {
    setEditingTemple(null);
    setIsFormOpen(true);
  };

  const handleEdit = (temple: Temple) => {
    setEditingTemple(temple);
    setIsFormOpen(true);
  };

  const handleSubmit = async (data: any) => {
    if (editingTemple) {
      await updateMutation.mutateAsync({ id: editingTemple.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить этот храм?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleActive = (id: number) => {
    toggleActiveMutation.mutate(id);
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Noto Serif' }}>
          Храмы
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Добавить храм
        </Button>
      </Box>

      <TemplesToolbar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
      />

      <TemplesTable
        temples={filteredTemples}
        loading={isLoading}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
      />

      <TemplesForm
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleSubmit}
        initialData={editingTemple as any}
        loading={createMutation.isPending || updateMutation.isPending}
        error={createMutation.error?.message || updateMutation.error?.message}
      />

      <Snackbar
        open={!!notification}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.type === 'success' ? 'success' : 'error'}
          sx={{ width: '100%' }}
          icon={notification?.type === 'success' ? <CheckCircle /> : <ErrorIcon />}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
